import { Picker } from '@react-native-picker/picker';
import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams, usePathname } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, Share, TextInput, TouchableOpacity, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundBattlePlans, useBackgroundEngagements, useBackgroundLeafletingRoutes, useBackgroundOrbat, useBackgroundPollingStations } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useNavigationStore } from '@/stores/navigationStore';
import { Engagement } from '@/types/engagement';
import { LeafletingRouteAssignment } from '@/types/leafleting';
import { authenticatedFetch } from '@/utils/auth';
import { getDynamicEnv } from '@/utils/configManager';

export default function SharePage() {
  const pathname = usePathname();
  const params = useLocalSearchParams();
  const { getSelectedBranch } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const hasGrantPermission = ['commander', 'lieutenant'].includes((selectedBranch?.branch_role || '').toLowerCase());
  const [copied, setCopied] = useState(false);
  
  // Theme-aware colors from Colors.ts
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  const textColor = useThemeColor({}, 'text');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const warningTextColor = useThemeColor({}, 'warningText');
  const errorTextColor = useThemeColor({}, 'errorText');
  const inputBackgroundColor = useThemeColor({}, 'inputBackground');
  const inputBorderColor = useThemeColor({}, 'inputBorder');
  
  // Get share settings from navigation store
  const shareSettings = useNavigationStore(state => state.shareSettings);
  const setShareSettings = useNavigationStore(state => state.setShareSettings);
  
  // Local state for share settings (synced with store)
  const [numUses, setNumUses] = useState(shareSettings.numUses);
  const [accessDuration, setAccessDuration] = useState(shareSettings.accessDuration);
  const [codeExpires, setCodeExpires] = useState(shareSettings.codeExpires);
  const [grantCode, setGrantCode] = useState<string | null>(null);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [selectedEngagements, setSelectedEngagements] = useState<string[]>([]);
  const [battlePlanName, setBattlePlanName] = useState<string>('');
  
  // Leafleting state
  const [selectedRouteAssignments, setSelectedRouteAssignments] = useState<string[]>([]);
  const [roundName, setRoundName] = useState<string>('');
  const [selectedCampaignerId, setSelectedCampaignerId] = useState<string>('');
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [grantAccessError, setGrantAccessError] = useState<string | null>(null);
  
  const insets = useSafeAreaInsets();
  
  // Fetch orbat (campaigners) for the branch
  const { data: orbat, loading: loadingOrbat } = useBackgroundOrbat(selectedBranch?.branch_short_name || '');
  
  // Helper function to get role level (same as in settings)
  const getRoleLevel = (role: string | null): number => {
    if (!role) return 0;
    const ROLE_HIERARCHY: { [key: string]: number } = {
      'Commander': 4,
      'Lieutenant': 3,
      'Sgt Major': 2,
      'Operator': 1,
      '': 0,
    };
    const normalized = role
      .toLowerCase()
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return ROLE_HIERARCHY[normalized] || 0;
  };

  // Group by user, select best entry per user, exclude officers, and filter by 6-month access
  const campaigners = React.useMemo(() => {
    if (!orbat) return [];
    const officerRoles = ['commander', 'lieutenant', 'sgt major'];
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    // First, filter by 6-month access window
    const recentEntries = orbat.filter((entry: any) => {
      // Include if no expiration date (still active)
      if (!entry.access_expires) return true;
      
      // Include if expiration is in the future (still active)
      const expiresDate = new Date(entry.access_expires);
      if (expiresDate > now) return true;
      
      // Include if expiration is within the last 6 months (had access recently)
      if (expiresDate >= sixMonthsAgo) return true;
      
      // Exclude if expired more than 6 months ago
      return false;
    });
    
    // Group by user (campaigner_id or email)
    const userGroups = new Map<string, any[]>();
    
    recentEntries.forEach((entry: any) => {
      const userId = entry.campaigner_id || entry.email;
      if (!userGroups.has(userId)) {
        userGroups.set(userId, []);
      }
      userGroups.get(userId)!.push(entry);
    });
    
    // For each user, find the best entry (highest permission, longest expiry)
    const bestEntries: any[] = [];
    
    userGroups.forEach((entries) => {
      if (entries.length === 0) return;
      
      const firstEntry = entries[0];
      if (!firstEntry) return;
      
      let bestEntry = firstEntry;
      let bestLevel = getRoleLevel(bestEntry.branch_role);
      
      for (const entry of entries) {
        const entryLevel = getRoleLevel(entry.branch_role);
        if (entryLevel > bestLevel) {
          bestEntry = entry;
          bestLevel = entryLevel;
        } else if (entryLevel === bestLevel) {
          // Same level, compare expiry dates (longest expiry wins)
          const bestExpiry = bestEntry.access_expires ? new Date(bestEntry.access_expires).getTime() : 0;
          const entryExpiry = entry.access_expires ? new Date(entry.access_expires).getTime() : 0;
          
          if (entryExpiry > bestExpiry) {
            bestEntry = entry;
          }
        }
      }
      
      // Filter out users with officer roles
      const role = (bestEntry.branch_role || '').toLowerCase();
      if (!officerRoles.includes(role)) {
        bestEntries.push(bestEntry);
      }
    });
    
    return bestEntries;
  }, [orbat]);

  // Extract parameters
  const battlePlanId = Array.isArray(params['battlePlanId']) ? params['battlePlanId'][0] : params['battlePlanId'];
  const engagementId = Array.isArray(params['engagementId']) ? params['engagementId'][0] : params['engagementId'];
  const roundId = Array.isArray(params['roundId']) ? params['roundId'][0] : params['roundId'];
  const assignmentId = Array.isArray(params['assignmentId']) ? params['assignmentId'][0] : params['assignmentId'];
  const pollingStationId = Array.isArray(params['pollingStationId']) ? params['pollingStationId'][0] : params['pollingStationId'];
  
  // Plan type state
  const [planType, setPlanType] = useState<'canvass' | 'telling' | 'leafleting' | null>(null);
  
  // Polling station state (now managed through engagement selection)
  
  // Fetch battle plans to determine plan type based on battle_plan_type
  const { data: battlePlans } = useBackgroundBattlePlans();
  
  // Determine plan type based on URL parameters and battle plan data
  useEffect(() => {
    if (pollingStationId) {
      setPlanType('telling');
    } else if (roundId) {
      setPlanType('leafleting');
    } else if (battlePlanId && battlePlans) {
      // Find the battle plan and check its type
      const battlePlan = battlePlans.find((bp: any) => bp.battle_plan_id === battlePlanId);
      if (battlePlan) {
        if (battlePlan.battle_plan_type === 'gotv-telling') {
          setPlanType('telling');
        } else if (battlePlan.battle_plan_type === 'canvassing' || battlePlan.battle_plan_type === 'gotv-door-knocking') {
          setPlanType('canvass');
        } else {
          // Default to canvass for unknown types
          setPlanType('canvass');
        }
      } else {
        // Battle plan not found, default to canvass
        setPlanType('canvass');
      }
    } else {
      setPlanType(null);
    }
  }, [pollingStationId, roundId, battlePlanId, battlePlans]);

  // Use background fetch for polling stations (only for telling plans)
  const shouldFetchPollingStations = planType === 'telling' && battlePlanId && battlePlanId !== '';
  const { data: pollingStations, loading: loadingPollingStations, error: pollingStationsError } = useBackgroundPollingStations(shouldFetchPollingStations ? battlePlanId : '');
  
  // Use background fetch for engagements (only for canvass plans)
  const shouldFetchEngagements = planType === 'canvass' && battlePlanId && battlePlanId !== '';
  const { data: engagements, loading: loadingEngagements, error: engagementsError } = useBackgroundEngagements(shouldFetchEngagements ? battlePlanId : '');
  
  // Use background fetch for leafleting routes (only for leafleting plans)
  const shouldFetchRoutes = planType === 'leafleting' && roundId && roundId !== '';
  const { data: routeAssignments, loading: loadingRouteAssignments, error: routeAssignmentsError } = useBackgroundLeafletingRoutes(shouldFetchRoutes ? roundId : '');

  // Handle polling station pre-selection when data is loaded
  useEffect(() => {
    if (pollingStationId && pollingStations && pollingStations.length > 0) {
      // Extract engagement IDs from the selected polling station
      const station = pollingStations.find((ps: any) => ps.polling_station_id.toString() === pollingStationId);
      if (station && station.polling_station_districts) {
        const engagementIds = station.polling_station_districts.map((district: any) => district.engagement_id);
        setSelectedEngagements(engagementIds);
      }
    }
  }, [pollingStationId, pollingStations]);

  // Handle engagement selection and battle plan name when data is loaded
  useEffect(() => {
    if (engagements && engagements.length > 0) {
      // Set battle plan name from first engagement
      setBattlePlanName(engagements[0].battle_plan_name);
      
      // If engagementId is provided, pre-select it; otherwise select all engagements
      if (engagementId) {
        setSelectedEngagements([engagementId]);
      } else {
        setSelectedEngagements(engagements.map((engagement: Engagement) => engagement.engagement_id));
      }
    }
  }, [engagements, engagementId]);

  // Handle route assignment selection and round name when data is loaded
  useEffect(() => {
    if (routeAssignments && routeAssignments.length > 0) {
      // Set round name from first route assignment
      setRoundName(routeAssignments[0].round_name);
      
      // If assignmentId is provided, pre-select it; otherwise select all route assignments
      if (assignmentId) {
        setSelectedRouteAssignments([assignmentId]);
      } else {
        setSelectedRouteAssignments(routeAssignments.map((assignment: LeafletingRouteAssignment) => assignment.assignment_id));
      }
    }
  }, [routeAssignments, assignmentId]);

  // Helper to convert access duration to seconds
  function accessDurationToSeconds(duration: string): number {
    if (duration === '12 hours') return 12 * 3600; // 12 hours = 43200 seconds
    if (duration === '1 day') return 86400;
    if (duration === '3 days') return 3 * 86400;
    if (duration === '1 week') return 7 * 86400;
    if (duration === '1 month') return 30 * 86400;
    return 12 * 3600; // Default to 12 hours
  }

  // Helper to get code_expires as ISO string in the future
  function codeExpiresToDate(expires: string): string {
    const now = new Date();
    if (expires === '1 hour') {
      now.setHours(now.getHours() + 1);
    } else if (expires === '1 day') {
      now.setDate(now.getDate() + 1);
    } else if (expires === '2 days') {
      now.setDate(now.getDate() + 2);
    } else if (expires === '3 days') {
      now.setDate(now.getDate() + 3);
    } else {
      now.setHours(now.getHours() + 1);
    }
    return now.toISOString().replace(/Z$/, '');
  }



  // Handle engagement selection
  const toggleEngagement = (engagementId: string) => {
    setSelectedEngagements(prev => 
      prev.includes(engagementId) 
        ? prev.filter(id => id !== engagementId)
        : [...prev, engagementId]
    );
  };

  // Handle route assignment selection
  const toggleRouteAssignment = (assignmentId: string) => {
    setSelectedRouteAssignments(prev => 
      prev.includes(assignmentId) 
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]
    );
  };

  // Handle polling station selection based on engagement selection
  const togglePollingStation = (stationId: string) => {
    if (!pollingStations) return;
    
    const station = pollingStations.find((ps: any) => ps.polling_station_id.toString() === stationId);
    if (!station || !station.polling_station_districts) return;
    
    const stationEngagementIds = station.polling_station_districts.map((district: any) => district.engagement_id);
    
    // Check if all engagements for this polling station are currently selected
    const allStationEngagementsSelected = stationEngagementIds.every((engId: string) => selectedEngagements.includes(engId));
    
    if (allStationEngagementsSelected) {
      // Remove all engagements for this polling station
      setSelectedEngagements(prev => prev.filter(engId => !stationEngagementIds.includes(engId)));
    } else {
      // Add all engagements for this polling station
      setSelectedEngagements(prev => {
        const newEngagements = [...prev];
        stationEngagementIds.forEach((engId: string) => {
          if (!newEngagements.includes(engId)) {
            newEngagements.push(engId);
          }
        });
        return newEngagements;
      });
    }
  };

  // Check if a polling station is selected (all its engagements are selected)
  const isPollingStationSelected = (stationId: string) => {
    if (!pollingStations) return false;
    
    const station = pollingStations.find((ps: any) => ps.polling_station_id.toString() === stationId);
    if (!station || !station.polling_station_districts) return false;
    
    const stationEngagementIds = station.polling_station_districts.map((district: any) => district.engagement_id);
    return stationEngagementIds.every((engId: string) => selectedEngagements.includes(engId));
  };

  // Fetch grant code when fields change
  useEffect(() => {
    // Don't fetch grant code if user doesn't have permission
    if (!hasGrantPermission) {
      setGrantCode(null);
      setGrantError(null);
      setGrantLoading(false);
      return;
    }

    setGrantLoading(true);
    setGrantError(null);
    setGrantCode(null);
    let cancelled = false;
    async function fetchGrantCode() {
      setGrantLoading(true);
      setGrantError(null);
      setGrantCode(null);
      try {
        const payload: any = {
          use_limit: parseInt(numUses, 10),
          code_expires: codeExpiresToDate(codeExpires),
          access_duration_seconds: accessDurationToSeconds(accessDuration),
        };

        if (battlePlanId && (planType === 'canvass' || planType === 'telling') && selectedEngagements.length > 0) {
          // Canvass battle plan engagement access
          payload.access_scope = {
            scope_description: "Access to specific battle plans and engagements",
            battle_plans: [
              {
                battle_plan_id: battlePlanId,
                engagements: selectedEngagements
              }
            ]
          };
        } else if (roundId && selectedRouteAssignments.length > 0) {
          // Leafleting round access
          payload.access_scope = {
            scope_description: "Access to specific leafleting rounds and route assignments",
            rounds: [
              {
                round_id: roundId,
                route_assignments: selectedRouteAssignments
              }
            ]
          };
        } else {
          // Operator role access (when no specific access is specified)
          payload.branch_role = 'Operator';
        }

        const res = await authenticatedFetch(
          selectedBranch?.branch_short_name,
          '/access-grants/create-grant-code',
          {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
          },
          'groundforce'
        );
        if (!res.ok) throw new Error('Failed to create grant code');
        const data = await res.json();
        if (!cancelled) setGrantCode(data.token);
      } catch (e: any) {
        if (!cancelled) setGrantError(e.message || 'Error creating grant code');
      } finally {
        if (!cancelled) setGrantLoading(false);
      }
    }
    fetchGrantCode();
    return () => { cancelled = true; };
  }, [numUses, accessDuration, codeExpires, selectedBranch?.branch_short_name, battlePlanId, selectedEngagements, roundId, selectedRouteAssignments, planType, hasGrantPermission]);

  // Sync local state with store on mount
  useEffect(() => {
    setNumUses(shareSettings.numUses);
    setAccessDuration(shareSettings.accessDuration);
    setCodeExpires(shareSettings.codeExpires);
  }, [shareSettings]);

  // Update store when local state changes
  useEffect(() => {
    setShareSettings({
      numUses,
      accessDuration,
      codeExpires,
    });
  }, [numUses, accessDuration, codeExpires, setShareSettings]);

  // Use associatedDomain as the base URL
  const [baseUrl, setBaseUrl] = useState<string>('');
  
  // Get dynamic associated domain
  useEffect(() => {
    const getBaseUrl = async () => {
      try {
        const dynamicEnv = await getDynamicEnv();
        setBaseUrl('https://' + dynamicEnv.associatedDomain);
      } catch (error) {
        console.error('Error getting dynamic environment:', error);
        // Fallback to default domain
        setBaseUrl('https://groundforce.dev.votes.now');
      }
    };
    getBaseUrl();
  }, []);
  let refParam = params['ref'];
  if (Array.isArray(refParam)) refParam = refParam[0];
  const referringUrl = refParam
    ? baseUrl + refParam
    : baseUrl + pathname;

  const shareUrl = grantCode
    ? referringUrl + (referringUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(grantCode)
    : referringUrl;

  // Compute QR code size: fit within the card (maxWidth 400px - padding 32px = 368px max)
  const cardMaxWidth = 350;
  const cardPadding = 32; // 16px on each side
  const qrSize = cardMaxWidth - cardPadding;

  const handleCopy = async () => {
    await Clipboard.setStringAsync(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleShare = async () => {
    try {
      await Share.share({ message: shareUrl });
    } catch (e) {
      alert(e);
      handleCopy();
    }
  };

  // Don't render until baseUrl is loaded
  if (!baseUrl) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ThemedText>Loading...</ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{ flex: 1 }}>
      {hasGrantPermission ? (
        <ScrollView contentContainerStyle={{ padding: 24, alignItems: 'center', flexGrow: 1, paddingBottom: insets.bottom }}>
          
          {/* Access Information Display */}
          <View style={{ 
            width: '100%', 
            maxWidth: 400, 
            backgroundColor: cardBackgroundColor, 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 20,
            borderWidth: 1,
            borderColor: borderColor
          }}>
            <ThemedText style={{ 
              fontSize: 16, 
              fontWeight: 'bold', 
              marginBottom: 12,
              textAlign: 'center'
            }}>
              Access Being Granted
            </ThemedText>
            {planType ? (
              <>
                <View style={{ marginBottom: 8 }}>
                  <ThemedText style={{ fontSize: 14, fontWeight: 'bold' }}>
                    {planType === 'canvass' ? (battlePlanName || 'Loading...') : 
                     planType === 'telling' ? 'Polling Stations' : 
                     planType === 'leafleting' ? (roundName || 'Loading...') : 'Loading...'}
                  </ThemedText>
                </View>
                
                {/* Canvass Plan Content */}
                {planType === 'canvass' && (
                  <>
                    {loadingEngagements ? (
                      <ThemedText style={{ fontSize: 14, color: secondaryTextColor, fontStyle: 'italic' }}>Loading engagements...</ThemedText>
                    ) : engagementsError ? (
                      <ThemedText style={{ fontSize: 14, color: errorTextColor }}>Error loading engagements: {engagementsError.message}</ThemedText>
                    ) : engagementId ? (
                      // When sharing from a specific engagement, just show the engagement name
                      <View style={{
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        backgroundColor: cardBackgroundColor,
                        borderRadius: 8
                      }}>
                        <ThemedText style={{ fontSize: 14, fontWeight: '500' }}>
                          {engagements?.find((e: Engagement) => e.engagement_id === engagementId)?.number_within_battle_plan}
                          {engagements?.find((e: Engagement) => e.engagement_id === engagementId)?.engagement_name ? 
                            `: ${engagements.find((e: Engagement) => e.engagement_id === engagementId)?.engagement_name}` : ''}
                        </ThemedText>
                      </View>
                        ) : (
                      // When sharing from engagement list, show checkboxes for selection
                      <>
                        {engagements?.sort((a: Engagement, b: Engagement) => 
                          (a.number_within_battle_plan || 0) - (b.number_within_battle_plan || 0)
                        ).map((engagement: Engagement) => {
                          const isSelected = selectedEngagements.includes(engagement.engagement_id);
                          
                          return (
                            <TouchableOpacity
                              key={engagement.engagement_id}
                              onPress={() => toggleEngagement(engagement.engagement_id)}
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingVertical: 8,
                                borderBottomWidth: 1,
                                borderBottomColor: borderColor
                              }}
                            >
                              <View style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 2,
                                borderColor: '#007AFF',
                                backgroundColor: isSelected ? '#007AFF' : 'transparent',
                                marginRight: 12,
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {isSelected && (
                                  <IconSymbol name="checkmark" size={14} color="#fff" />
                                )}
                              </View>
                              <ThemedText style={{ fontSize: 14, flex: 1 }}>
                                {engagement.number_within_battle_plan}{engagement.engagement_name ? `: ${engagement.engagement_name}` : ''}
                              </ThemedText>
                            </TouchableOpacity>
                          );
                        })}
                        {selectedEngagements.length === 0 && (
                          <ThemedText style={{ fontSize: 14, color: errorTextColor, fontStyle: 'italic', marginTop: 8 }}>
                            Please select at least one engagement
                          </ThemedText>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Telling Plan Content */}
                {planType === 'telling' && (
                  <>
                    {loadingPollingStations ? (
                      <ThemedText style={{ fontSize: 14, color: secondaryTextColor, fontStyle: 'italic' }}>Loading polling stations...</ThemedText>
                    ) : pollingStationsError ? (
                      <ThemedText style={{ fontSize: 14, color: errorTextColor }}>Error loading polling stations: {pollingStationsError.message}</ThemedText>
                    ) : pollingStationId ? (
                      // When sharing from a specific polling station, just show the station name
                      <View style={{
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        backgroundColor: cardBackgroundColor,
                        borderRadius: 8
                      }}>
                        <ThemedText style={{ fontSize: 14, fontWeight: '500' }}>
                          {pollingStations?.find((s: any) => s.polling_station_id.toString() === pollingStationId)?.station_name}
                        </ThemedText>
                      </View>
                    ) : (
                      // When sharing from polling station list, show checkboxes for selection
                      <>
                        {pollingStations?.sort((a: any, b: any) => a.station_name.localeCompare(b.station_name)).map((station: any) => {
                          const isSelected = isPollingStationSelected(station.polling_station_id.toString());
                          
                          return (
                            <TouchableOpacity
                              key={station.polling_station_id}
                              onPress={() => togglePollingStation(station.polling_station_id.toString())}
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingVertical: 8,
                                borderBottomWidth: 1,
                                borderBottomColor: borderColor
                              }}
                            >
                              <View style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 2,
                                borderColor: '#007AFF',
                                backgroundColor: isSelected ? '#007AFF' : 'transparent',
                                marginRight: 12,
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {isSelected && (
                                  <IconSymbol name="checkmark" size={14} color="#fff" />
                                )}
                              </View>
                              <ThemedText style={{ fontSize: 14, flex: 1 }}>
                                {station.station_name}
                              </ThemedText>
                            </TouchableOpacity>
                          );
                        })}
                        {selectedEngagements.length === 0 && (
                          <ThemedText style={{ fontSize: 14, color: errorTextColor, fontStyle: 'italic', marginTop: 8 }}>
                            Please select at least one polling station
                          </ThemedText>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : null}
            
            {/* Leafleting Plan Content */}
            {planType === 'leafleting' && (
              <>
                {loadingRouteAssignments ? (
                  <ThemedText style={{ fontSize: 14, color: secondaryTextColor, fontStyle: 'italic' }}>Loading route assignments...</ThemedText>
                ) : routeAssignmentsError ? (
                  <ThemedText style={{ fontSize: 14, color: errorTextColor }}>Error loading route assignments: {routeAssignmentsError.message}</ThemedText>
                ) : assignmentId ? (
                  // When sharing from a specific assignment, just show the assignment name
                  <View style={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    backgroundColor: cardBackgroundColor,
                    borderRadius: 8
                  }}>
                    <ThemedText style={{ fontSize: 14, fontWeight: '500' }}>
                      {routeAssignments?.find((a: LeafletingRouteAssignment) => a.assignment_id === assignmentId)?.number_within_branch}
                      {routeAssignments?.find((a: LeafletingRouteAssignment) => a.assignment_id === assignmentId)?.route_name ? 
                        `: ${routeAssignments.find((a: LeafletingRouteAssignment) => a.assignment_id === assignmentId)?.route_name}` : ''}
                    </ThemedText>
                  </View>
                ) : (
                  // When sharing from assignment list, show checkboxes for selection
                  <>
                    {routeAssignments?.sort((a: LeafletingRouteAssignment, b: LeafletingRouteAssignment) => 
                      (a.number_within_branch || 0) - (b.number_within_branch || 0)
                    ).map((assignment: LeafletingRouteAssignment) => {
                      const isSelected = selectedRouteAssignments.includes(assignment.assignment_id);
                      
                      return (
                        <TouchableOpacity
                          key={assignment.assignment_id}
                          onPress={() => toggleRouteAssignment(assignment.assignment_id)}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 8,
                            borderBottomWidth: 1,
                            borderBottomColor: borderColor
                          }}
                        >
                          <View style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 2,
                            borderColor: '#007AFF',
                            backgroundColor: isSelected ? '#007AFF' : 'transparent',
                            marginRight: 12,
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {isSelected && (
                              <IconSymbol name="checkmark" size={14} color="#fff" />
                            )}
                          </View>
                          <ThemedText style={{ fontSize: 14, flex: 1 }}>
                            {assignment.number_within_branch}{assignment.route_name ? `: ${assignment.route_name}` : ''}
                          </ThemedText>
                        </TouchableOpacity>
                      );
                    })}
                    {selectedRouteAssignments.length === 0 && (
                      <ThemedText style={{ fontSize: 14, color: errorTextColor, fontStyle: 'italic', marginTop: 8 }}>
                        Please select at least one route assignment
                      </ThemedText>
                    )}
                  </>
                )}
              </>
            )}
            
            {!planType && (
              <>
                <View style={{ marginBottom: 8 }}>
                  <ThemedText style={{ fontSize: 14, color: secondaryTextColor, fontWeight: '500' }}>Type:</ThemedText>
                  <ThemedText style={{ fontSize: 14, fontWeight: 'bold' }}>Operator Role Access</ThemedText>
                </View>
                <ThemedText style={{ fontSize: 14, color: secondaryTextColor, fontStyle: 'italic' }}>
                  Full operator access to all branch functions
                </ThemedText>
              </>
            )}
            
            {/* Access Duration - applies to both grant methods */}
            <View style={{ marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: borderColor }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <ThemedText style={[AppStyles.filterLabel, { minWidth: 120, marginRight: 10 }]}>Access Duration</ThemedText>
                <View style={[AppStyles.pickerWrapper, { flex: 1, minWidth: 140, backgroundColor: inputBackgroundColor, borderColor: inputBorderColor }]}> 
                    <Picker
                    selectedValue={accessDuration}
                    onValueChange={setAccessDuration}
                    style={[AppStyles.pickerInput, { color: textColor }]}
                    dropdownIconColor="#007AFF"
                    >
                    <Picker.Item label="12 hours" value="12 hours" />
                    <Picker.Item label="1 day" value="1 day" />
                    <Picker.Item label="3 days" value="3 days" />
                    <Picker.Item label="1 week" value="1 week" />
                    <Picker.Item label="1 month" value="1 month" />
                    </Picker>
                </View>
              </View>
            </View>
          </View>
          
          {/* Grant by Campaigner Section */}
          <View style={{ 
            width: '100%', 
            maxWidth: 400, 
            backgroundColor: cardBackgroundColor, 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 20,
            borderWidth: 1,
            borderColor: borderColor
          }}>
            <ThemedText style={{ 
              fontSize: 18, 
              fontWeight: 'bold', 
              marginBottom: 16,
              textAlign: 'center'
            }}>
              Grant by Campaigner
            </ThemedText>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
              <ThemedText style={[AppStyles.filterLabel, { minWidth: 120, marginRight: 10 }]}>Campaigner</ThemedText>
              <View style={[AppStyles.pickerWrapper, { flex: 1, minWidth: 140, backgroundColor: inputBackgroundColor, borderColor: inputBorderColor }]}> 
                  <Picker
                  selectedValue={selectedCampaignerId}
                  onValueChange={setSelectedCampaignerId}
                  style={[AppStyles.pickerInput, { color: textColor }]}
                  dropdownIconColor="#007AFF"
                  enabled={!loadingOrbat && campaigners.length > 0}
                  >
                  <Picker.Item label={loadingOrbat ? "Loading..." : "Select campaigner"} value="" />
                  {campaigners.map((entry: any) => (
                    <Picker.Item 
                      key={entry.campaigner_id} 
                      label={entry.name || entry.email} 
                      value={entry.campaigner_id} 
                    />
                  ))}
                  </Picker>
              </View>
            </View>
            
            {/* Grant Access Button */}
            {grantAccessError && (
              <ThemedText style={{ color: errorTextColor, textAlign: 'center', marginBottom: 10 }}>
                {grantAccessError}
              </ThemedText>
            )}
            <TouchableOpacity
              onPress={async () => {
                  if (!selectedBranch?.branch_short_name || !selectedCampaignerId) {
                    setGrantAccessError('Please select a campaigner');
                    return;
                  }

                  const selectedCampaigner = campaigners.find((entry: any) => entry.campaigner_id === selectedCampaignerId);
                  if (!selectedCampaigner) {
                    setGrantAccessError('Campaigner not found');
                    return;
                  }

                  try {
                    setGrantingAccess(true);
                    setGrantAccessError(null);

                    // Build access scope based on current selections
                    let accessScope: any = {
                      scope_description: "Access to specific resources",
                    };

                    if (battlePlanId && (planType === 'canvass' || planType === 'telling') && selectedEngagements.length > 0) {
                      // Canvass battle plan engagement access
                      accessScope.battle_plans = [
                        {
                          battle_plan_id: battlePlanId,
                          engagements: selectedEngagements
                        }
                      ];
                    } else if (roundId && selectedRouteAssignments.length > 0) {
                      // Leafleting round access
                      accessScope.rounds = [
                        {
                          round_id: roundId,
                          route_assignments: selectedRouteAssignments
                        }
                      ];
                    } else {
                      // Full access (all battle plans and rounds)
                      accessScope.battle_plans = ["*"];
                      accessScope.rounds = ["*"];
                    }

                    // Calculate access_expires based on accessDuration
                    const now = new Date();
                    const expiresDate = new Date(now.getTime() + (accessDurationToSeconds(accessDuration) * 1000));

                    const payload = {
                      campaigner_id: selectedCampaigner.campaigner_id,
                      email: selectedCampaigner.email,
                      access_scope: accessScope,
                      access_expires: expiresDate.toISOString(),
                    };

                    const response = await authenticatedFetch(
                      selectedBranch.branch_short_name,
                      '/orbat/assign-access-scope',
                      {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                      },
                      'query' // Not 'groundforce'
                    );

                    if (!response.ok) {
                      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
                    }

                    // Success - clear selection and show success message
                    setSelectedCampaignerId('');
                    alert('Access granted successfully');
                  } catch (error: any) {
                    console.error('Error granting access:', error);
                    setGrantAccessError(error.message || 'Failed to grant access');
                  } finally {
                    setGrantingAccess(false);
                  }
                }}
                style={[
                  AppStyles.primaryButton,
                  {
                    opacity: (grantingAccess || !selectedCampaignerId) ? 0.5 : 1,
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }
                ]}
                disabled={grantingAccess || !selectedCampaignerId}
              >
                {grantingAccess ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <IconSymbol name="person.badge.plus" size={22} color="#fff" />
                    <ThemedText style={[AppStyles.primaryButtonText, { marginLeft: 8 }]} lightColor="#fff" darkColor="#fff">
                      Grant Access
                    </ThemedText>
                  </>
                )}
              </TouchableOpacity>
          </View>
          
          {/* Grant by Code Section */}
          <View style={{ 
            width: '100%', 
            maxWidth: 400, 
            backgroundColor: cardBackgroundColor, 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 20,
            borderWidth: 1,
            borderColor: borderColor
          }}>
            <ThemedText style={{ 
              fontSize: 18, 
              fontWeight: 'bold', 
              marginBottom: 16,
              textAlign: 'center'
            }}>
              Grant by Code
            </ThemedText>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <ThemedText style={[AppStyles.filterLabel, { minWidth: 120, marginRight: 10 }]}>Code Valid for</ThemedText>
              <View style={[AppStyles.pickerWrapper, { flex: 1, minWidth: 140, backgroundColor: inputBackgroundColor, borderColor: inputBorderColor }]}> 
                  <Picker
                  selectedValue={codeExpires}
                  onValueChange={setCodeExpires}
                  style={[AppStyles.pickerInput, { color: textColor }]}
                  dropdownIconColor="#007AFF"
                  >
                  <Picker.Item label="1 hour" value="1 hour" />
                  <Picker.Item label="1 day" value="1 day" />
                  <Picker.Item label="2 days" value="2 days" />
                  <Picker.Item label="3 days" value="3 days" />
                  </Picker>
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
              <ThemedText style={[AppStyles.filterLabel, { minWidth: 120, marginRight: 10 }]}>Number of uses</ThemedText>
              <View style={[AppStyles.pickerWrapper, { flex: 1, minWidth: 140, backgroundColor: inputBackgroundColor, borderColor: inputBorderColor }]}> 
                  <Picker
                  selectedValue={numUses}
                  onValueChange={setNumUses}
                  style={[AppStyles.pickerInput, { color: textColor }]}
                  dropdownIconColor="#007AFF"
                  >
                  {Array.from({ length: 100 }, (_, i) => (
                      <Picker.Item key={i + 1} label={`${i + 1}`} value={`${i + 1}`} />
                  ))}
                  </Picker>
              </View>
            </View>
            
            {/* Grant code display */}
            {grantLoading && <ThemedText style={{ textAlign: 'center', marginBottom: 10 }}>Generating code...</ThemedText>}
            {grantError && <ThemedText style={{ color: errorTextColor, textAlign: 'center', marginBottom: 10 }}>{grantError}</ThemedText>}
            {grantCode && (
                <>
                    <View style={{ alignItems: 'center', marginBottom: 24, marginTop: 8 }}>
                        <QRCode value={shareUrl} size={qrSize} />
                    </View>
                
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20}}>
                    <TextInput
                        style={[AppStyles.textInput, { flex: 1, marginRight: 8, backgroundColor: inputBackgroundColor, borderColor: inputBorderColor, color: textColor }]}
                        value={shareUrl}
                        editable={false}
                        selectTextOnFocus={true}
                        autoFocus={false}
                    />
                    </View>
                    {Platform.OS !== 'web' ? (
                        <TouchableOpacity onPress={handleShare} style={[AppStyles.primaryButton, { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginBottom: 16 }]}> 
                            <IconSymbol name="square.and.arrow.up" size={22} color="#fff" />
                            <ThemedText style={[AppStyles.primaryButtonText, { marginLeft: 8 }]} lightColor="#fff" darkColor="#fff">Share</ThemedText>
                        </TouchableOpacity>
                        ) : (
                        <TouchableOpacity onPress={handleCopy} style={AppStyles.linkButton}>
                            <IconSymbol name="square.and.arrow.up" size={22} color="#007AFF" />
                            <ThemedText style={AppStyles.linkButtonText}>{copied ? 'Copied!' : 'Copy'}</ThemedText>
                        </TouchableOpacity>
                    )}
                </>
            )}
          </View>
        </ScrollView>
      ) : (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <ThemedText style={{ color: warningTextColor, fontSize: 18, textAlign: 'center' }}>
            You do not have permissions to grant access to this branch
          </ThemedText>
        </View>
      )}
    </ThemedView>
  );
} 